import { ChangeEvent, FC, useState } from 'react'
import { useForm } from 'react-hook-form'
import zod from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import errorIcon from '@wellesley/ui/images/note_corner.svg'

import { Form, FormField, cn } from '@wellesley/ui'
import { useSelector } from 'react-redux'
import {
  userSlice,
  useDeleteApiV1NotesByUserIdMutation,
  usePutApiV1NotesMutation,
} from '@wellesley/user'
import { authSlice } from '@wellesley/auth'
import useResizableNote from './hooks/useResizableNote'

type CmpProps = {
  userId: string
  note?: string
  className?: string
  resizable?: boolean
}

const DEFAULT_HEIGHT = 52
const MAX_CHARS = 200
const CHAR_LIMIT_WARNING = 10

const formSchema = zod.object({
  note: zod.string().max(MAX_CHARS),
})

const UserNote: FC<CmpProps> = ({
  userId,
  note: noteArg = '',
  className,
  resizable = false,
}) => {
  const [setNote] = usePutApiV1NotesMutation()
  const [deleteNote] = useDeleteApiV1NotesByUserIdMutation()

  const [isFocused, setIsFocused] = useState(false)

  const isLogged = useSelector(authSlice.selectors.selectIsAuthenticated)
  const currentUser = useSelector(userSlice.selectors.selectCurrentUser)
  const isOwnUser = currentUser?.id === userId

  const onBlur = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newNote = e.target.value
    setIsFocused(false)

    void updateNoteAndRelations(newNote)
  }

  const updateNoteAndRelations = async (newNote = '') => {
    newNote
      ? await setNote({
          noteUpdate: {
            userId,
            text: newNote,
          },
        })
      : await deleteNote({
          userId,
        })
  }

  const form = useForm({
    resolver: zodResolver(formSchema),
  })

  const {
    inputRef,
    textareaRef,
    charCount,
    setCharCount,
    handleTextareaChange,
    handleKeyDown,
  } = useResizableNote(noteArg)

  if (isOwnUser || !isLogged) return

  const canShowCharsWarning =
    charCount >= MAX_CHARS - CHAR_LIMIT_WARNING && isFocused

  return (
    <Form {...form}>
      <form className={cn('mt-m', className)} onKeyDown={handleKeyDown}>
        {canShowCharsWarning && (
          <div className="mb-xxs text-right text-sm text-gray-400">
            {MAX_CHARS - charCount} characters left
          </div>
        )}
        <FormField
          control={form.control}
          name="note"
          render={(field) => (
            <div className="rounded-m relative flex items-center bg-gray-100  text-gray-700">
              {resizable ? (
                <textarea
                  {...field}
                  ref={textareaRef}
                  maxLength={MAX_CHARS}
                  onBlur={onBlur}
                  onFocus={() => setIsFocused(true)}
                  onChange={handleTextareaChange}
                  defaultValue={noteArg}
                  placeholder="Leave a note (visible only to you)"
                  className="scrollbar-hide p-s w-full resize-none border-0 bg-transparent text-base leading-5 outline-none [-ms-overflow-style:none] [scrollbar-width:none] focus:border-0 focus-visible:border-0 focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-[1] [&::-webkit-scrollbar]:hidden"
                  style={{ height: `${DEFAULT_HEIGHT}px` }}
                />
              ) : (
                <input
                  {...field}
                  ref={inputRef}
                  maxLength={MAX_CHARS}
                  onBlur={onBlur}
                  onFocus={() => setIsFocused(true)}
                  onChange={(e) => {
                    setCharCount(e.target.value.length)
                  }}
                  defaultValue={noteArg}
                  placeholder="Leave a note visible only to you"
                  className="p-s w-full truncate border-0 bg-transparent focus-visible:outline-none disabled:opacity-[1]"
                  style={{ height: `${DEFAULT_HEIGHT}px` }}
                />
              )}
              <img
                src={errorIcon}
                alt="user note"
                className="absolute bottom-0 right-0"
              />
            </div>
          )}
        />
      </form>
    </Form>
  )
}

export default UserNote
